/**
 * Map Types & Interfaces
 * Phase 1.5 of map maintainability refactor
 *
 * These interfaces replace ~104 uses of `any` type in map components
 * to improve type safety and IDE support.
 */

import Graphic from '@arcgis/core/Graphic';
import Extent from '@arcgis/core/geometry/Extent';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';

// ============================================================================
// Core Map Types
// ============================================================================

/** Date range for filtering map data */
export interface MapDateRange {
  start: Date;
  end: Date;
}

/** Screen coordinates for tooltips and popups */
export interface ScreenPoint {
  x: number;
  y: number;
}

/** Layer visibility settings */
export interface MapLayerSettings {
  siteBoundary: boolean;
  picture: boolean;
  asBuilt: boolean;
  bidItem: boolean;
  activity: boolean;
  delivery: boolean;
  forms?: boolean;
  warrantyClaim?: boolean;
}

// ============================================================================
// Marker Types
// ============================================================================

/** Base marker interface with common properties */
export interface BaseMarker {
  id: string;
  latitude: number | null;
  longitude: number | null;
  location?: {
    lat: number;
    lng: number;
  };
}

/** Site marker for the main map */
export interface SiteMarker extends BaseMarker {
  site_id: string;
  site_name: string;
  last_updated_at?: string;
  isFiltered?: boolean;
  data?: SiteData;
}

/** Picture marker for fullscreen mode */
export interface PictureMarker extends BaseMarker {
  uuid: string;
  thumbnail_url?: string;
  picture_url?: string;
  created_at?: string;
  site_id?: string;
  daily_report_id?: string;
  bid_item_id?: string;
}

/** Bid item marker */
export interface BidItemMarker extends BaseMarker {
  bid_item_id: string;
  bid_item_name: string;
  quantity?: number;
  unit?: string;
  site_id?: string;
  daily_report_id?: string;
}

/** Activity marker */
export interface ActivityMarker extends BaseMarker {
  activity_id: string;
  activity_name?: string;
  description?: string;
  site_id?: string;
  daily_report_id?: string;
}

/** Delivery marker */
export interface DeliveryMarker extends BaseMarker {
  delivery_id: string;
  delivery_name?: string;
  quantity?: number;
  site_id?: string;
  daily_report_id?: string;
}

/** Unified marker type for clustering layer */
export type UnifiedMarker = PictureMarker | BidItemMarker | ActivityMarker | DeliveryMarker;

// ============================================================================
// Site & Project Types
// ============================================================================

/** Site data structure */
export interface SiteData {
  site_id: string;
  site_name: string;
  project_id?: string;
  latitude?: number;
  longitude?: number;
  last_updated_at?: string;
  isFiltered?: boolean;
  geometry?: GeoJsonGeometry;
}

/** Project data structure */
export interface ProjectData {
  project_id: string;
  project_name: string;
  base_maps?: BasemapConfig[];
  sites?: SiteData[];
}

// ============================================================================
// Geometry Types
// ============================================================================

/** GeoJSON geometry types */
export type GeoJsonGeometryType = 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon';

/** GeoJSON geometry structure */
export interface GeoJsonGeometry {
  type: GeoJsonGeometryType;
  coordinates: number[] | number[][] | number[][][] | number[][][][];
}

/** Report geometry with metadata */
export interface ReportGeometry {
  geometry: GeoJsonGeometry;
  site_id?: string;
  daily_report_id?: string;
  bid_item_id?: string;
  geometry_type?: 'site_boundary' | 'as_built' | 'picture_location';
  color?: string;
}

/** Bounding coordinates for extent calculation */
export interface BoundingCoordinates {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

// ============================================================================
// Popup Types
// ============================================================================

/** Item types for popups */
export type PopupItemType = 'picture' | 'bid_item' | 'activity' | 'delivery';

/** Popup item content */
export interface PopupItem {
  type: PopupItemType;
  id: string;
  name?: string;
  thumbnail_url?: string;
  description?: string;
  quantity?: number;
  unit?: string;
  created_at?: string;
  data?: Record<string, unknown>;
}

/** Selected item data for detail view */
export interface SelectedItemData {
  type: PopupItemType;
  id: string;
  name?: string;
  site_id?: string;
  daily_report_id?: string;
  [key: string]: unknown;
}

// ============================================================================
// Basemap Types
// ============================================================================

/** Basemap configuration */
export interface BasemapConfig {
  id: string;
  name: string;
  thumbnail_url?: string;
  basemap_type: 'esri' | 'custom';
  url?: string;
}

// ============================================================================
// Multi-Item Location Types
// ============================================================================

/** Grouped items at a single location */
export interface MultiItemLocationGroup {
  pictures: PictureMarker[];
  bidItems: BidItemMarker[];
  activities: ActivityMarker[];
  deliveries: DeliveryMarker[];
}

/** Same location picture group */
export interface SameLocationGroup {
  location: string;
  pictures: PictureMarker[];
}

// ============================================================================
// Map Event Types
// ============================================================================

/** Map click event */
export interface MapClickEvent {
  screenPoint: ScreenPoint;
  mapPoint?: __esri.Point;
  graphic?: Graphic;
}

/** Marker click event */
export interface MarkerClickEvent {
  screenPoint: ScreenPoint;
  graphic: Graphic;
  attributes: Record<string, unknown>;
}

/** Tooltip event */
export interface TooltipEvent {
  screenPoint?: ScreenPoint;
  graphic?: Graphic;
}

// ============================================================================
// Map Component Instance Types
// ============================================================================

/** Esri Map Component instance (from EsriMapComponent) */
export interface MapComponentInstance {
  mapViewInstance: __esri.MapView;
  mapInstance: __esri.Map;
  mapProjectGraphicsLayer: GraphicsLayer;
  mapMultiGeoGraphicsLayer: GraphicsLayer;
  mapMarkerGraphicsLayer: GraphicsLayer;
}

// ============================================================================
// Filter Types
// ============================================================================

/** Bid item filter state */
export interface BidItemFilterState {
  selectedIds?: string[];
  showAll: boolean;
}

// ============================================================================
// Utility Types
// ============================================================================

/** Color map for sites */
export type SiteColorMap = Record<string, string>;

/** Marker pictures map */
export type MarkerPicturesMap = Map<string, PictureMarker[]>;

/** Multi-item locations map */
export type MultiItemLocationsMap = Map<string, MultiItemLocationGroup>;

// ============================================================================
// Label Types
// ============================================================================

/** Available label field options */
export type LabelByOption = 'bid_item_name' | 'quantity' | 'bid_item_code' | 'site_name' | 'title';

/** Label configuration for map markers */
export interface MapLabelConfig {
  enabled: boolean;
  labelBy: LabelByOption;
  // Embedded Form 266 viewer only: when true, quantity labels are aggregated to
  // ONE label per bid item at the combined centroid of all its shapes, using the
  // bid item's effective quantity once (not summed-per-piece). The main map keeps
  // its default per-geometry quantity labels (each piece is a distinct datapoint).
  aggregateQuantityPerBidItem?: boolean;
}

export type RenderMode = 'pins' | 'clustered' | 'heatmap';

export type SwipePresetId =
  | 'cip-vs-existing-streets'
  | 'design-vs-bike-trails'
  | 'project-design-vs-asbuilt'
  | 'off';

export interface ReferenceLayerToolbarEntry {
  id: string;
  name: string;
  /** Published catalog group name; omitted for the legacy flat registry. */
  group?: string;
  opacity?: number;
  labels?: boolean;
  sourceLabel?: string;
  status?: 'idle' | 'loading' | 'ready' | 'error';
  error?: string;
  extent?: { xmin: number; ymin: number; xmax: number; ymax: number } | null;
  /** A placed drawing: Open sheet, and the follow-current badge */
  drawing?: { sheetUuid: string; revisionUuid: string; number: string; label: string | null; needsPlacement: boolean };
}

export interface MapToolbarState {
  renderMode: RenderMode;
  swipePreset: SwipePresetId;
  swipeAvailable: boolean;
  spatialSelectActive: boolean;
  /** Reference layers available for overlay toggling (project-registered). */
  overlayLayers: ReferenceLayerToolbarEntry[];
  /** Subset of overlayLayers ids that are currently toggled visible. */
  visibleOverlayIds: string[];
  overlayLoading?: boolean;
  overlayError?: string;
}

export const DEFAULT_MAP_TOOLBAR_STATE: MapToolbarState = {
  renderMode: 'pins',
  swipePreset: 'off',
  swipeAvailable: false,
  spatialSelectActive: false,
  overlayLayers: [],
  visibleOverlayIds: [],
};
